import { memo, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { QueryRule as QueryRuleType, QueryField, OperatorValue } from '../../../../types/QueryBuilder';
import { getFieldsByCategory, getOperatorsForField } from '../../../../utils/queryBuilderUtils';
import { alpha, type Theme } from '@mui/material/styles';
import { useLanguage } from '../../../../context/LanguageContext';
import { primarySoft } from '../../../../theme';
import ValueEditor from './ValueEditors';
import RuleSelect from './RuleSelect';

interface QueryRuleProps {
    rule: QueryRuleType;
    categories: string[];
    fields: QueryField[];
    onUpdateRule: (ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (ruleId: string) => void;
}

const ruleSx = {
    display: 'flex',
    alignItems: 'stretch',
    gap: 1,
    mb: 2,
    p: { xs: 1, sm: 1.5 },
    bgcolor: 'background.paper',
    borderRadius: 1,
    border: '1px solid', borderColor: 'divider',
    '&:hover': { borderColor: 'primary.main', boxShadow: (t: Theme) => `0 2px 8px ${primarySoft(t, 0.1)}` },
};

const QueryRule = memo(function QueryRule({ rule, categories, fields, onUpdateRule, onRemoveRule }: QueryRuleProps) {
    const { t } = useLanguage();
    const { id, category, field: fieldName, operator, locked = false } = rule;

    const field = useMemo(
        () => fields.find(f => f.name === fieldName && f.Categorie === category),
        [fields, fieldName, category]
    );
    const categoryOptions = useMemo(() => categories.map(value => ({ value, label: value })), [categories]);
    const fieldOptions = useMemo(
        () => (category ? getFieldsByCategory(fields, category) : []).map(f => ({ value: f.name, label: f.label })),
        [fields, category]
    );
    const operatorOptions = useMemo(
        () => getOperatorsForField(field).map(op => ({ value: op.value, label: t(`pages.clients.queryBuilder.operators.${op.labelKey}`) })),
        [field, t]
    );

    const handleCategoryChange = (newCategory: string) => onUpdateRule(id, {
        category: newCategory,
        field: getFieldsByCategory(fields, newCategory)[0]?.name || '',
        operator: '=',
        value: '',
        valueTo: undefined,
    });

    const handleFieldChange = (newFieldName: string) => {
        const newField = fields.find(f => f.name === newFieldName && f.Categorie === category);
        onUpdateRule(id, {
            field: newFieldName,
            operator: (newField?.defaultOperator as OperatorValue) || '=',
            value: '',
            valueTo: undefined,
        });
    };

    const handleOperatorChange = (newOperator: string) => onUpdateRule(id, {
        operator: newOperator as OperatorValue,
        ...(newOperator !== 'between' && { valueTo: undefined }),
    });

    return (
        <Box sx={ruleSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tooltip title={t(locked ? 'pages.clients.queryBuilder.unlockRule' : 'pages.clients.queryBuilder.lockRule')}>
                    <IconButton
                        size="small"
                        onClick={() => onUpdateRule(id, { locked: !locked })}
                        sx={{ color: locked ? 'primary.main' : 'text.secondary', '&:hover': { backgroundColor: (t) => primarySoft(t, 0.1) } }}
                    >
                        {locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                    <RuleSelect
                        label={t('pages.clients.queryBuilder.category')}
                        value={category || ''}
                        options={categoryOptions}
                        disabled={locked}
                        onChange={handleCategoryChange}
                    />
                    <RuleSelect
                        label={t('pages.clients.queryBuilder.field')}
                        value={fieldName || ''}
                        options={fieldOptions}
                        disabled={!category || locked}
                        onChange={handleFieldChange}
                    />
                    <RuleSelect
                        label={t('pages.clients.queryBuilder.operator')}
                        value={operator || ''}
                        options={operatorOptions}
                        disabled={!fieldName || locked}
                        onChange={handleOperatorChange}
                    />
                </Box>

                {field && operator && (
                    <Box
                        sx={{
                            flex: '1 1 240px',
                            minWidth: '200px',
                            maxWidth: '100%',
                            ...(locked && { pointerEvents: 'none', opacity: 0.6 }),
                        }}
                    >
                        <ValueEditor
                            ruleId={id}
                            field={field}
                            operator={operator}
                            value={rule.value}
                            onChange={value => onUpdateRule(id, { value })}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Tooltip title={t('pages.clients.queryBuilder.deleteRule')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => onRemoveRule(id)}
                            disabled={locked}
                            sx={{ color: 'error.main', '&:hover': { backgroundColor: (t) => alpha(t.palette.error.main, 0.1) } }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
});

export default QueryRule;
